import type { IVREdge, IVRFlow, IVRNode } from "@/types/IVRFlow"

import { createEmptyFlow, uniqueNodeId } from "./ivr-flow-utils"

/** The mutually-exclusive interaction mode a node is in. A node is exactly one
 * of: auto-advances to a single target, has outgoing match edges, or is a
 * terminal leaf. */
export type NodeKind = "auto_advance" | "edges" | "terminal"

/** Derive the editor "kind" tab for a node from its shape. Outgoing edge count
 * is passed in because the edges live on the flow, not the node. */
export const detectNodeKind = (
  node: IVRNode,
  outgoingCount: number,
): NodeKind => {
  if (node.terminal) {
    return "terminal"
  }
  if (outgoingCount > 0) {
    return "edges"
  }
  return "auto_advance"
}

export const updateNodePropsInFlow = (
  flow: IVRFlow,
  nodeId: string,
  patch: Partial<IVRNode>,
): IVRFlow => ({
  ...flow,
  nodes: flow.nodes.map((n) => (n.id === nodeId ? { ...n, ...patch } : n)),
})

/** Switch a node between terminal / auto-advance / edges modes, keeping the
 * flow's "exactly one mode" invariant. Switching away from edges strips this
 * node's outgoing edges; switching into edges seeds one empty edge so the
 * validator has something to flag. */
export const setNodeKindInFlow = (
  flow: IVRFlow,
  nodeId: string,
  kind: NodeKind,
): IVRFlow => {
  const otherIds = flow.nodes.filter((n) => n.id !== nodeId).map((n) => n.id)
  const updatedNodes = flow.nodes.map((n) => {
    if (n.id !== nodeId) {
      return n
    }
    if (kind === "terminal") {
      return { ...n, terminal: true, auto_advance_to: undefined }
    }
    if (kind === "auto_advance") {
      return {
        ...n,
        terminal: false,
        auto_advance_to: n.auto_advance_to ?? otherIds[0] ?? "",
      }
    }
    // kind === "edges"
    return { ...n, terminal: false, auto_advance_to: undefined }
  })
  let updatedEdges = flow.edges
  if (kind !== "edges") {
    // Strip outgoing edges from this node when switching away from edges mode.
    updatedEdges = flow.edges.filter((e) => e.source !== nodeId)
  } else if (!flow.edges.some((e) => e.source === nodeId)) {
    // Seed one empty edge when entering edges mode so the validator is happy.
    updatedEdges = [
      ...flow.edges,
      { source: nodeId, input: "", destination: otherIds[0] ?? "" },
    ]
  }
  return { ...flow, nodes: updatedNodes, edges: updatedEdges }
}

/** Append a blank outgoing edge for a source node. Defaults the destination to
 * the first other node when one isn't given. */
export const addEdgeToFlow = (
  flow: IVRFlow,
  sourceId: string,
  destinationId?: string,
): IVRFlow => {
  const otherIds = flow.nodes.filter((n) => n.id !== sourceId).map((n) => n.id)
  const newEdge: IVREdge = {
    source: sourceId,
    input: "",
    destination: destinationId ?? otherIds[0] ?? "",
  }
  return { ...flow, edges: [...flow.edges, newEdge] }
}

export const updateEdgeInFlow = (
  flow: IVRFlow,
  index: number,
  patch: Partial<IVREdge>,
): IVRFlow => ({
  ...flow,
  edges: flow.edges.map((e, i) => (i === index ? { ...e, ...patch } : e)),
})

export const removeEdgeFromFlow = (flow: IVRFlow, index: number): IVRFlow => ({
  ...flow,
  edges: flow.edges.filter((_, i) => i !== index),
})

/** Wire up a connection dragged on the canvas from source to target.
 *
 * The behavior depends on the source node's current mode:
 * - edges mode: append a new edge (deduping an existing blank edge to target).
 * - terminal or empty: switch the node to auto-advance to the target.
 * - auto-advance: replace the auto target (single-target nodes feel right).
 *
 * Returns the original flow unchanged for self-connections or unknown sources. */
export const connectNodesInFlow = (
  flow: IVRFlow,
  sourceId: string,
  targetId: string,
): IVRFlow => {
  if (sourceId === targetId) {
    return flow
  }
  const sourceNode = flow.nodes.find((n) => n.id === sourceId)
  if (!sourceNode) {
    return flow
  }
  const sourceHasEdges = flow.edges.some((e) => e.source === sourceId)
  const isAutoAdvance = !!sourceNode.auto_advance_to && !sourceHasEdges
  const isTerminal = !!sourceNode.terminal

  if (sourceHasEdges) {
    const alreadyEmpty = flow.edges.some(
      (e) =>
        e.source === sourceId && e.input === "" && e.destination === targetId,
    )
    if (alreadyEmpty) {
      return flow
    }
    return {
      ...flow,
      edges: [
        ...flow.edges,
        { source: sourceId, input: "", destination: targetId },
      ],
    }
  }
  if (isTerminal || !isAutoAdvance) {
    return {
      ...flow,
      nodes: flow.nodes.map((n) =>
        n.id === sourceId
          ? { ...n, terminal: false, auto_advance_to: targetId }
          : n,
      ),
    }
  }
  return {
    ...flow,
    nodes: flow.nodes.map((n) =>
      n.id === sourceId ? { ...n, auto_advance_to: targetId } : n,
    ),
  }
}

/** A new blank terminal node plus the id assigned to it, added to the flow.
 * When the prior flow is null a fresh empty flow seeded with just this node
 * (as the start node) is returned. */
interface AddBlankNodeResult {
  flow: IVRFlow
  nodeId: string
}

export const addBlankNodeToFlow = (
  flow: IVRFlow | null,
): AddBlankNodeResult => {
  const base = flow ?? createEmptyFlow()
  const newId = uniqueNodeId(base, "node")
  const newNode: IVRNode = {
    id: newId,
    prompt: "",
    terminal: true,
  }
  const nextFlow = flow
    ? { ...flow, nodes: [...flow.nodes, newNode] }
    : { ...base, nodes: [newNode], start_node: newId }
  return { flow: nextFlow, nodeId: newId }
}

/** Remove a node and scrub every dangling reference to it: outgoing/incoming
 * edges, auto_advance_to pointers, and the start_node (which falls back to the
 * first remaining node). */
export const deleteNodeFromFlow = (flow: IVRFlow, nodeId: string): IVRFlow => {
  const remaining = flow.nodes.filter((n) => n.id !== nodeId)
  const cleanedNodes = remaining.map((n) => ({
    ...n,
    auto_advance_to:
      n.auto_advance_to === nodeId ? undefined : n.auto_advance_to,
  }))
  const cleanedEdges = flow.edges.filter(
    (e) => e.source !== nodeId && e.destination !== nodeId,
  )
  return {
    ...flow,
    start_node:
      flow.start_node === nodeId
        ? (cleanedNodes[0]?.id ?? "")
        : flow.start_node,
    nodes: cleanedNodes,
    edges: cleanedEdges,
  }
}

/** Rename a node id everywhere it is referenced: the node itself, start_node,
 * auto_advance_to pointers, and edge source/destination. */
export const renameNodeInFlow = (
  flow: IVRFlow,
  oldId: string,
  nextId: string,
): IVRFlow => ({
  ...flow,
  start_node: flow.start_node === oldId ? nextId : flow.start_node,
  nodes: flow.nodes.map((n) => ({
    ...n,
    id: n.id === oldId ? nextId : n.id,
    auto_advance_to: n.auto_advance_to === oldId ? nextId : n.auto_advance_to,
  })),
  edges: flow.edges.map((e) => ({
    ...e,
    source: e.source === oldId ? nextId : e.source,
    destination: e.destination === oldId ? nextId : e.destination,
  })),
})

/** Move a node's position key under a new id in a positions map. Returns the
 * original map unchanged when the old id has no recorded position. */
export const renamePosition = (
  positions: Record<string, { x: number; y: number }>,
  oldId: string,
  nextId: string,
): Record<string, { x: number; y: number }> => {
  if (!(oldId in positions)) {
    return positions
  }
  const { [oldId]: pos, ...rest } = positions
  return { ...rest, [nextId]: pos }
}
